import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useMediators } from '@/hooks/useMediators';
import { Search, User, Loader2, Star, Lock } from 'lucide-react';
import { ACTIVE_MEDIATION_AREAS, getMediationAreaLabel } from '@/lib/mediationAreaMapping';
import { useAuth } from '@/contexts/AuthContext';

interface MediatorSearchWidgetProps {
  defaultMediationArea?: string;
}

export const MediatorSearchWidget = ({ defaultMediationArea = '' }: MediatorSearchWidgetProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchFilters, setSearchFilters] = useState({
    city: '',
    mediationArea: defaultMediationArea,
  });

  const { data: mediators, isLoading: mediatorsLoading } = useMediators(searchFilters);
  
  // For non-authenticated users, show only top 3 results
  const displayLimit = user ? 5 : 3;
  const displayedMediators = mediators?.slice(0, displayLimit) || [];

  return (
    <Card className="bg-card/95 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Search className="h-5 w-5" />
          Trouvez un médiateur
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Search Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="widget-city">Ville</Label>
            <Input
              id="widget-city"
              placeholder="Ex: Paris"
              value={searchFilters.city}
              onChange={(e) => setSearchFilters(prev => ({ ...prev, city: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="widget-area">Domaine de médiation</Label>
            <Select
              value={searchFilters.mediationArea}
              onValueChange={(value) => setSearchFilters(prev => ({ ...prev, mediationArea: value === 'all' ? '' : value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Tous les domaines" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les domaines</SelectItem>
                {ACTIVE_MEDIATION_AREAS.map((area) => (
                  <SelectItem key={area.value} value={area.value}>{area.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Results */}
        <div className="space-y-3">
          {mediatorsLoading && (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
              <p className="text-sm text-muted-foreground">Recherche de médiateurs...</p>
            </div>
          )}

          {!mediatorsLoading && mediators?.length === 0 && (
            <div className="text-center py-8">
              <User className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Aucun médiateur trouvé</p>
              <Button 
                variant="link" 
                size="sm"
                onClick={() => setSearchFilters({ city: '', mediationArea: '' })}
                className="mt-2"
              >
                Réinitialiser les filtres
              </Button>
              {!user && (
                <Button
                  onClick={() => navigate('/connexion?mode=signup')}
                  className="mt-3 w-full"
                >
                  Inscrivez-vous pour plus de résultats
                </Button>
              )}
            </div>
          )}

          {!mediatorsLoading && mediators && mediators.length > 0 && (
            <>
              <div className="max-h-96 overflow-y-auto space-y-3 pr-2">
                {displayedMediators.map((mediator) => (
                  <Card key={mediator.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="font-semibold flex items-center gap-2">
                            <User className="h-4 w-4" />
                            {mediator.profiles?.full_name}
                            {!user && (
                              <Lock className="h-3 w-3 text-muted-foreground" />
                            )}
                          </h4>
                          <p className="text-xs text-muted-foreground">{mediator.profiles?.city}</p>
                        </div>
                        {mediator.is_verified && (
                          <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">✓</span>
                        )}
                      </div>
                      
                      <div className="space-y-1 mb-3">
                        <p className="text-xs">
                          <strong>Domaines:</strong> {mediator.mediation_areas?.slice(0, 2).map(getMediationAreaLabel).join(', ')}
                          {mediator.mediation_areas?.length > 2 && '...'}
                        </p>
                        {mediator.rating && mediator.rating > 0 && (
                          <div className="flex items-center gap-1">
                            <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                            <span className="text-xs font-semibold">{mediator.rating.toFixed(1)}</span>
                            <span className="text-xs text-muted-foreground">/ 5</span>
                          </div>
                        )}
                      </div>

                      <Button
                        size="sm"
                        onClick={() => navigate(user ? '/trouver-un-mediateur' : '/connexion?mode=signup')}
                        className="w-full"
                      >
                        {user ? 'Voir le profil' : 'Se connecter pour voir'}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Sign up CTA for non-authenticated users */}
              {!user && mediators.length > displayLimit && (
                <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
                  <CardContent className="p-4 text-center">
                    <Lock className="h-8 w-8 mx-auto mb-2 text-primary" />
                    <p className="font-semibold text-foreground mb-1">
                      +{mediators.length - displayLimit} autres médiateurs disponibles
                    </p>
                    <p className="text-sm text-muted-foreground mb-3">
                      Inscrivez-vous pour voir tous les profils et prendre rendez-vous
                    </p>
                    <Button
                      onClick={() => navigate('/connexion?mode=signup')}
                      className="w-full"
                    >
                      Inscrivez-vous pour continuer
                    </Button>
                  </CardContent>
                </Card>
              )}

              {user && mediators.length > displayLimit && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/trouver-un-mediateur')}
                  className="w-full"
                >
                  Voir tous les médiateurs ({mediators.length})
                </Button>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
